/**
 * NotificationsTableRow - Linha da tabela de notificações
 * Memoizado para evitar re-renders desnecessários
 */

import React, { memo } from 'react';
import {
  TableRow,
  TableCell,
  Box,
  Typography,
  Avatar,
  Chip,
  Checkbox,
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';

import { usePremiumTokens } from '../../common/theme/usePremiumTokens';
import { useTranslation } from '../../common/components/LocalizationProvider';
import { prefixString } from '../../common/util/stringUtils';
import { formatBoolean } from '../../common/util/formatter';
import CollectionActions from './CollectionActions';

// Helper para cores do status - USA TOKENS DINÂMICOS
const getStatusColors = (status, tokens) => {
  const colors = {
    active: {
      text: tokens?.semantic?.success,
      bg: tokens?.semantic?.successBg,
      border: `${tokens?.semantic?.success}40`,
    },
    disabled: {
      text: tokens?.semantic?.error,
      bg: tokens?.semantic?.errorBg,
      border: `${tokens?.semantic?.error}40`,
    },
  };

  return colors[status] || colors.active;
};

const NotificationsTableRow = memo(({
  row,
  onRefresh,
  selected = false,
  onSelect,
}) => {
  const t = useTranslation();
  const tokens = usePremiumTokens();

  const status = row.disabled ? 'disabled' : 'active';
  const statusColors = getStatusColors(status, tokens);

  // Cor do avatar baseada no status
  const avatarColor = statusColors.text;

  // Formatação dos notificadores
  const formatList = (prefix, value) => {
    if (value) {
      return value
        .split(/[, ]+/)
        .filter(Boolean)
        .map((it) => t(prefixString(prefix, it)))
        .join(', ');
    }
    return '-';
  };

  return (
    <TableRow
      hover
      selected={selected}
      sx={{
        cursor: 'default',
        transition: tokens?.transitions?.colors || 'all 0.15s ease',
        '&:hover': {
          backgroundColor: tokens?.surface?.hover || 'rgba(255,255,255,0.03)',
        },
        '&.Mui-selected': {
          backgroundColor: `${tokens?.accent?.primary}10`,
          '&:hover': {
            backgroundColor: `${tokens?.accent?.primary}15`,
          },
        },
      }}
    >
      {/* Checkbox */}
      {onSelect && (
        <TableCell padding="checkbox" sx={{ py: 1, width: 48 }}>
          <Checkbox
            checked={selected}
            onChange={() => onSelect(row.id)}
            sx={{
              color: tokens?.text?.tertiary,
              '&.Mui-checked': {
                color: tokens?.accent?.primary,
              },
            }}
          />
        </TableCell>
      )}

      {/* Avatar */}
      <TableCell sx={{ py: 1, width: 48 }}>
        <Avatar
          sx={{
            width: 32,
            height: 32,
            bgcolor: `${avatarColor}20`,
            color: avatarColor,
          }}
        >
          <NotificationsIcon fontSize="small" />
        </Avatar>
      </TableCell>

      {/* ID */}
      <TableCell sx={{ py: 1, width: 60 }}>
        <Typography variant="body2" sx={{ color: tokens?.text?.muted, fontFamily: 'monospace', fontSize: '0.8rem' }}>
          {row.id}
        </Typography>
      </TableCell>

      {/* Type */}
      <TableCell sx={{ py: 1 }}>
        <Typography variant="body2" fontWeight={500}>
          {t(prefixString('event', row.type))}
        </Typography>
      </TableCell>

      {/* Always */}
      <TableCell sx={{ py: 1 }}>
        <Chip
          label={formatBoolean(row.always, t)}
          size="small"
          sx={{
            height: 22,
            fontSize: '0.7rem',
            fontWeight: 600,
            backgroundColor: row.always ? tokens?.semantic?.successBg : tokens?.surface?.elevated,
            color: row.always ? tokens?.semantic?.success : tokens?.text?.secondary,
            border: `1px solid ${row.always ? `${tokens?.semantic?.success}40` : tokens?.border?.subtle}`,
          }}
        />
      </TableCell>

      {/* Notificators */}
      <TableCell sx={{ py: 1, maxWidth: 200 }}>
        <Typography
          variant="body2"
          color="text.secondary"
          noWrap
          sx={{ fontSize: '0.8rem', textOverflow: 'ellipsis' }}
        >
          {formatList('notificator', row.notificators)}
        </Typography>
      </TableCell>

      {/* Status */}
      <TableCell align="center" sx={{ py: 1 }}>
        <Chip
          label={row.disabled ? t('sharedDisabled') : t('deviceStatusActive')}
          size="small"
          className={row.disabled ? 'status-disabled' : 'status-active'}
          sx={{
            minWidth: 70,
            height: 22,
            fontSize: '0.7rem',
            fontWeight: 600,
            backgroundColor: statusColors.bg,
            color: statusColors.text,
            border: `1px solid ${statusColors.border}`,
            '& .MuiChip-label': { px: 1 },
          }}
        />
      </TableCell>

      {/* Actions */}
      <TableCell align="right" sx={{ py: 1 }}>
        <CollectionActions
          itemId={row.id}
          editPath="/settings/notification"
          endpoint="notifications"
          setTimestamp={onRefresh}
        />
      </TableCell>
    </TableRow>
  );
});

NotificationsTableRow.displayName = 'NotificationsTableRow';

export default NotificationsTableRow;
